'use client';

import type { EonAccount, Viewer } from '@repo/api-gw-sdk';
import { setTag, setUser, setExtra } from '@sentry/nextjs';
import {
  createContext,
  useEffect,
  useState,
  type PropsWithChildren,
} from 'react';

import { PreferencesKey } from '@/data/dal/preferencesKey';

import { useNonProjectDal } from './useNonProjectDal';

interface UserContextType {
  user: Viewer | undefined;
  eonAccount: EonAccount | undefined;
  isEmployee: boolean;
  currentProjectId: string;
  changeRole: (roleId: string) => Promise<unknown>;
  changeProject: (projectId: string) => Promise<void>;
}

export const UserContext = createContext<UserContextType | null>(null);

export const UserProvider = ({ children }: PropsWithChildren) => {
  const { nonProjectDal: dal } = useNonProjectDal();

  const [currentProjectId, setCurrentProjectId] = useState('');
  const { data: user, isLoading: isLoadingUser } = dal.users.getViewer();
  const { data: projects, isLoading: isLoadingProjects } = dal.projects.list();
  const { data: pref, isLoading: isLoadingPreferences } =
    dal.preferences.getUserPref(
      (projects?.projects || []).length > 1
        ? PreferencesKey.CurrentProject
        : undefined
    );

  useEffect(() => {
    if (isLoadingUser || isLoadingProjects || isLoadingPreferences || !user) {
      return;
    }

    let initialProjectId = '';
    if (user.selectedRole.isProjectRole) {
      if (user.roleToProjectsMappings[user.selectedRole.id].ids?.length) {
        initialProjectId =
          user.roleToProjectsMappings[user.selectedRole.id].ids![0];
      }
    } else {
      initialProjectId = projects?.projects?.[0]?.id || '';
    }

    if (
      pref?.value?.projectId &&
      projects?.projects?.some((x) => x.id === pref.value!.projectId)
    ) {
      if (
        !user.selectedRole.isProjectRole ||
        user.roleToProjectsMappings[user.selectedRole.id]?.ids?.includes(
          pref.value.projectId
        )
      ) {
        initialProjectId = pref.value.projectId;
      }
    }

    if (initialProjectId) {
      setCurrentProjectId(initialProjectId);
    }
  }, [
    isLoadingPreferences,
    isLoadingProjects,
    isLoadingUser,
    pref,
    projects,
    user,
  ]);

  useEffect(() => {
    // Enrich sentry errors
    setTag('projectId', currentProjectId);

    if (user) {
      setUser({
        id: user.id,
        email: user.email,
        name: user.givenName,
      });

      setTag('roleId', user.selectedRole.id);
      setTag('roleName', user.selectedRole.name);
      setTag('eonAccountId', user.eonAccount.id);

      setExtra('scopes', user.selectedRole.scopes);
    }
  }, [currentProjectId, user]);

  return (
    <UserContext.Provider
      value={{
        user,
        isEmployee: Boolean(
          user?.email?.endsWith('@eon.io') ||
            user?.givenName.endsWith('@eon.io')
        ),
        eonAccount: user?.eonAccount,
        currentProjectId,
        changeProject: (newProjectId) =>
          dal.preferences
            .updateUserPref(PreferencesKey.CurrentProject, {
              projectId: newProjectId,
            })
            .then(() => setCurrentProjectId(newProjectId)),
        changeRole: (roleId) =>
          dal.users
            .changeRole(roleId)
            .then(() => dal.users.refreshToken(roleId)),
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
